import { Controller } from "stimulus"

// Drives the single shared "create menu appearance" modal (rendered once in
// v2/menus/edit.html.erb). Any appearance-swatch select on the page - menu
// settings, category form, menu item advanced tab - can trigger it by
// dispatching "menu-appearance:create-requested" on document. Once the
// appearance is saved, the new <option> is appended to whichever select asked
// for it and selected there.
export default class extends Controller {
  static targets = ["name", "textColour", "backgroundColour", "previewBox", "submitButton", "errors"]
  static values = { url: String }

  connect() {
    this.pendingSelect = null
    this.handleCreateRequest = this.handleCreateRequest.bind(this)
    document.addEventListener("menu-appearance:create-requested", this.handleCreateRequest)

    this.$modal = typeof $ !== "undefined" ? $(this.element) : null
    if (!this.$modal) console.warn("[appearance-creator] jQuery/$ not available - modal cannot be shown")

    // Bootstrap 4.6 does not reference-count stacked modals: it uses a single
    // z-index for every modal/backdrop and strips body.modal-open on any hide.
    // Since this modal opens on top of #primary-modal (category / menu settings
    // forms), we correct the stack on show and restore the parent on hide.
    if (this.$modal) {
      this.$modal.on("shown.bs.modal", () => this.raiseAboveParents())
      this.$modal.on("hidden.bs.modal", () => this.restoreParentModal())
    }
  }

  disconnect() {
    document.removeEventListener("menu-appearance:create-requested", this.handleCreateRequest)
    if (this.$modal) this.$modal.off("shown.bs.modal hidden.bs.modal")
  }

  handleCreateRequest(event) {
    this.pendingSelect = event.detail.select
    this.reset()
    this.previewBoxTarget.textContent = event.detail.label || "Preview"
    this.show()
  }

  show() {
    if (this.$modal) {
      this.$modal.modal("show")
    } else {
      console.error("[appearance-creator] cannot open modal - no jQuery modal available")
    }
  }

  close() {
    if (this.$modal) this.$modal.modal("hide")
    this.pendingSelect = null
  }

  // Sit above whatever is already open: any parent Bootstrap modal
  // (e.g. #primary-modal) or the custom menu item drawer. Our backdrop is the
  // last one Bootstrap appended, so we tuck it just under our own modal but
  // above the parent.
  raiseAboveParents() {
    if (!this.$modal) return

    let base = 1050
    $(".modal.show").not(this.element).each((_, modalEl) => {
      base = Math.max(base, parseInt($(modalEl).css("z-index"), 10) || 1050)
    })
    if (document.body.classList.contains("menu-v2-drawer-open")) {
      base = Math.max(base, 1085)
    }

    const modalZ = base + 20
    this.$modal.css("z-index", modalZ)
    $(".modal-backdrop").last().css("z-index", modalZ - 10)
  }

  // Bootstrap removed body.modal-open when this modal hid; re-add it if another
  // Bootstrap modal is still open so the parent keeps its scroll lock. The
  // drawer manages its own body class, so we don't touch that case.
  restoreParentModal() {
    if ($(".modal.show").length) {
      document.body.classList.add("modal-open")
    }
  }

  reset() {
    this.nameTarget.value = ""
    this.textColourTarget.value = "#ffffff"
    this.backgroundColourTarget.value = "#000000"
    this.hideErrors()
    this.updatePreview()
  }

  updatePreview() {
    this.previewBoxTarget.style.backgroundColor = this.backgroundColourTarget.value
    this.previewBoxTarget.style.color = this.textColourTarget.value
  }

  hideErrors() {
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("d-none")
  }

  showErrors(messages) {
    this.errorsTarget.textContent = Array.isArray(messages) ? messages.join(", ") : messages
    this.errorsTarget.classList.remove("d-none")
  }

  async create() {
    console.log("[appearance-creator] create clicked")
    const name = this.nameTarget.value.trim()
    if (!name) {
      console.warn("[appearance-creator] name blank - aborting")
      this.showErrors("Please enter a name.")
      return
    }

    this.submitButtonTarget.disabled = true
    this.hideErrors()

    const payload = {
      menu_appearance: {
        name: name,
        text_colour: this.textColourTarget.value,
        background_colour: this.backgroundColourTarget.value,
      },
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        console.error("[appearance-creator] server rejected create", data)
        this.showErrors(data.errors || "Unable to create this appearance.")
        return
      }

      this.applyToSelect(data)
      this.close()
    } catch (error) {
      console.error("[appearance-creator] request failed", error)
      this.showErrors("Something went wrong. Please try again.")
    } finally {
      this.submitButtonTarget.disabled = false
    }
  }

  applyToSelect(appearance) {
    console.log("[appearance-creator] applyToSelect", { appearance, hasPendingSelect: !!this.pendingSelect })
    if (!this.pendingSelect) return

    const option = document.createElement("option")
    option.value = appearance.id
    option.textContent = appearance.name
    option.dataset.backgroundColour = appearance.background_colour
    option.dataset.textColour = appearance.text_colour
    this.pendingSelect.appendChild(option)

    const $select = typeof $ !== "undefined" ? $(this.pendingSelect) : null
    if ($select && $select.hasClass("select2-hidden-accessible")) {
      $select.val(String(appearance.id)).trigger("change")
    } else {
      this.pendingSelect.value = appearance.id
      this.pendingSelect.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ""
  }
}
